import { useState } from 'react';
import { useLogto } from '@logto/react';
import { apiPost, apiGet } from '../utils/api';
import API_ENDPOINTS from '../config/apiConfig';

interface QuickFixParams {
    [key: string]: any;
}

interface FixResult {
    success: boolean;
    changes_summary: string;
    updated_config?: any;
    updated_data_preview?: any[];
    before_state: Record<string, any>;
    after_state: Record<string, any>;
    warnings: string[];
}

export const useDiagnostics = () => {
    const [isApplyingFix, setIsApplyingFix] = useState(false);
    const [fixResult, setFixResult] = useState<FixResult | null>(null);
    const logto = useLogto();

    const runPreTrainingCheck = async (datasetId: string, config: any) => {
        try {
            return await apiPost<any>(API_ENDPOINTS.DIAGNOSTICS.PRE_TRAINING_CHECK, logto, {
                dataset_id: datasetId,
                config_dict: config
            });
        } catch (error) {
            console.error('Pre-training check error:', error);
            throw error;
        }
    };

    const applyQuickFix = async (
        datasetId: string,
        config: any,
        fixAction: string,
        params?: QuickFixParams
    ): Promise<FixResult> => {
        setIsApplyingFix(true);
        setFixResult(null);

        try {
            const result = await apiPost<FixResult>(API_ENDPOINTS.DIAGNOSTICS.QUICK_FIX_APPLY, logto, {
                dataset_id: datasetId,
                config,
                fix_action: fixAction,
                params: params || {}
            });

            setFixResult(result);
            return result;
        } catch (error) {
            console.error('Quick-fix error:', error);
            throw error;
        } finally {
            setIsApplyingFix(false);
        }
    };

    const getAvailableActions = async () => {
        try {
            return await apiGet<any[]>(API_ENDPOINTS.DIAGNOSTICS.QUICK_FIX_ACTIONS, logto);
        } catch (error) {
            console.error('Error fetching quick-fix actions:', error);
            throw error;
        }
    };

    return {
        runPreTrainingCheck,
        applyQuickFix,
        getAvailableActions,
        isApplyingFix,
        fixResult
    };
};
